import React, { FunctionComponent } from 'react';
import { ApolloError } from 'apollo-boost';
import { Grid, Card, Icon, Image } from 'semantic-ui-react';
import { IconSizeProp } from 'semantic-ui-react/src/elements/Icon/Icon';
import withStyles from 'react-jss';
import { map, equals, where, length, all, is, pathOr } from 'ramda';
import { FormattedDate } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';

import visaLogo from 'assets/icons/visa.png';
import mastercardLogo from 'assets/icons/mastercard.png';
import amexLogo from 'assets/icons/amex.svg';
import Query from 'components/Query';
import { CNStatusCardsPlaceholder } from 'components/Placeholder';
import { ClassProp } from 'helpers/types';
import Button from 'components/Button';

import styles from './CardSection.styles';
import GET_CARD_NETWORK_IDS from './CardSection.queries';
import AddCNIDModal from './sections/AddCNIDModal';
import RefreshCNIdModal from './sections/RefreshCNIdModal';

export type Cnid = {
  type: string;
  externalId: string;
  transactedAt: string;
  transacting: boolean;
};

type CardData = {
  updatedAt: string;
  cnids: Cnid[];
};

type CardNetworkIds = {
  amex: CardData;
  mastercard: CardData;
  visa: CardData;
};

type QueryResponse = {
  cardNetworkIds: {
    amex: CardData;
    mastercard: CardData;
    visa: CardData;
  };
  loading?: boolean;
  error?: ApolloError;
};

type Props = {
  variables: { venueUuid: string };
  classes: ClassProp<typeof styles>;
};

const whereTransacted = where({
  transacting: equals(true),
  transactedAt: is(String),
});

const getColorForCardNetworkFromCnids = (cnids: Cnid[]) => {
  const allCnidsTransacted = all(whereTransacted, cnids);

  if (!length(cnids)) {
    return 'red';
  }

  if (allCnidsTransacted) {
    return 'green';
  }

  return 'yellow';
};

const getColorFromCnid = (cnid: Cnid) => {
  if (whereTransacted(cnid)) {
    return 'green';
  }

  return 'red';
};

const renderStatusIcon = (
  cnids: Cnid | Cnid[],
  getColorFunc: Function,
  size?: IconSizeProp,
) => {
  const color = getColorFunc(cnids);

  return <Icon name="circle" color={color} size={size} />;
};

const renderCnids = map((cnid: Cnid) => {
  const { externalId, type } = cnid;

  return (
    <React.Fragment key={`${externalId}-${Math.random()}`}>
      <Grid.Column>{type}</Grid.Column>
      <Grid.Column textAlign="right">
        {renderStatusIcon(cnid, getColorFromCnid, 'small')}
        {externalId}
      </Grid.Column>
    </React.Fragment>
  );
});

const renderLastUpdated = (date: Date | string) =>
  date ? (
    <FormattedDate value={date} year="numeric" month="long" day="2-digit" />
  ) : (
    'N/A'
  );

const renderCardSection = (classes: ClassProp<typeof styles>) => (
  response: QueryResponse,
) => {
  const { amex, mastercard, visa } = pathOr(
    [],
    ['data', 'cardNetworkIds'],
    response,
  ) as CardNetworkIds;

  return (
    <Grid columns="equal">
      {response.loading ? (
        <>
          <CNStatusCardsPlaceholder />
          <CNStatusCardsPlaceholder />
          <CNStatusCardsPlaceholder />
        </>
      ) : (
        <Grid.Row>
          <Grid.Column className={classes.noPadding}>
            <Card className={classes.card}>
              <Card.Header className={classes.header}>
                <Grid.Row>
                  <Grid.Column width={8} floated="left">
                    <Image
                      className={classes.icon}
                      src={visaLogo}
                      avatar
                      rounded
                    />
                    Visa
                  </Grid.Column>
                  <Grid.Column width={8} floated="right">
                    {renderStatusIcon(
                      visa.cnids,
                      getColorForCardNetworkFromCnids,
                    )}
                  </Grid.Column>
                </Grid.Row>
              </Card.Header>

              <Card.Description className={classes.description}>
                <Grid>
                  <Grid.Row columns={2}>
                    <Grid.Column>Last Updated</Grid.Column>
                    <Grid.Column textAlign="right">
                      {renderLastUpdated(visa.updatedAt)}
                    </Grid.Column>

                    {renderCnids(visa.cnids)}
                  </Grid.Row>
                </Grid>
              </Card.Description>
            </Card>

            <AddCNIDModal
              title="Add by VSID"
              label="VSID"
              cardNetworkExternalIdType="VSID"
              cardNetwork="VISA"
              trigger={
                <Button className={classes.modalButton} title="Add by VSID" />
              }
            />

            <RefreshCNIdModal
              title="Refresh CNIDs"
              label="Refresh"
              cardNetworkExternalIdType="VSID"
              cardNetwork="VISA"
              trigger={
                <Button className={classes.modalButton} title="Refresh CNIDs" />
              }
            />
          </Grid.Column>

          <Grid.Column className={classes.noPadding}>
            <Card className={classes.card}>
              <Card.Header className={classes.header}>
                <Grid.Row>
                  <Grid.Column width={8} floated="left">
                    <Image
                      className={classes.icon}
                      src={mastercardLogo}
                      avatar
                      rounded
                    />
                    Mastercard
                  </Grid.Column>
                  <Grid.Column width={8} floated="right">
                    {renderStatusIcon(
                      mastercard.cnids,
                      getColorForCardNetworkFromCnids,
                    )}
                  </Grid.Column>
                </Grid.Row>
              </Card.Header>

              <Card.Description className={classes.description}>
                <Grid>
                  <Grid.Row columns={2}>
                    <Grid.Column>Last Updated</Grid.Column>
                    <Grid.Column textAlign="right">
                      {renderLastUpdated(mastercard.updatedAt)}
                    </Grid.Column>

                    {renderCnids(mastercard.cnids)}
                  </Grid.Row>
                </Grid>
              </Card.Description>
            </Card>

            <AddCNIDModal
              title="Add by LocationID"
              label="LocationID"
              cardNetworkExternalIdType="LID"
              cardNetwork="MASTERCARD"
              trigger={
                <Button
                  className={classes.modalButton}
                  title="Add by LocationID"
                />
              }
            />
          </Grid.Column>

          <Grid.Column className={classes.noPadding}>
            <Card className={classes.card}>
              <Card.Header className={classes.header}>
                <Grid.Row>
                  <Grid.Column width={8} floated="left">
                    <Grid.Row>
                      <Image
                        className={classes.icon}
                        src={amexLogo}
                        avatar
                        rounded
                      />
                      Amex
                    </Grid.Row>
                  </Grid.Column>
                  <Grid.Column width={8} floated="right">
                    {renderStatusIcon(
                      amex.cnids,
                      getColorForCardNetworkFromCnids,
                    )}
                  </Grid.Column>
                </Grid.Row>
              </Card.Header>

              <Card.Description className={classes.description}>
                <Grid>
                  <Grid.Row columns={2}>
                    <Grid.Column>Last Updated</Grid.Column>
                    <Grid.Column textAlign="right">
                      {renderLastUpdated(amex.updatedAt)}
                    </Grid.Column>

                    {renderCnids(amex.cnids)}
                  </Grid.Row>
                </Grid>
              </Card.Description>
            </Card>

            <AddCNIDModal
              title="Add by SE"
              label="SE"
              cardNetworkExternalIdType="SE"
              cardNetwork="AMEX"
              trigger={
                <Button className={classes.modalButton} title="Add by SE" />
              }
            />
          </Grid.Column>
        </Grid.Row>
      )}
    </Grid>
  );
};

const CardSection: FunctionComponent<Props> = ({ variables, classes }) => (
  <Query
    enableProgressInfo
    variables={variables}
    query={GET_CARD_NETWORK_IDS}
    render={renderCardSection(classes)}
  />
);

export default compose<Props, any>(withRouter, withStyles(styles))(CardSection);
